import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle, Circle, CircleNotch, Rocket, Sparkle, X } from '@phosphor-icons/react';

import {
  dismissGettingStarted,
  markGettingStartedSeen,
  skipGettingStartedStep,
} from '@/api/gettingStarted';
import {
  GETTING_STARTED_QUERY_KEY,
  MILESTONE_META,
  useGettingStarted,
} from '@/components/GettingStartedCard';
import { Button } from '@/components/ui/button';
import { askAssistant } from '@/lib/askAssistant';
import { fireConfetti } from '@/lib/confetti';
import { ScheduleGenerator } from '@/components/setup/generators/ScheduleGenerator';
import { WebsiteGenerator } from '@/components/setup/generators/WebsiteGenerator';

// Milestones whose CTA opens an in-wizard "generate in the background" flow
// instead of deep-linking to a settings page. Everything else navigates.
const CATEGORY_STEP = 'team_categories';
const WEBSITE_STEP = 'publish_website';
const SCHEDULE_STEPS = new Set(['create_template', 'apply_schedule']);
const GENERATOR_STEPS = new Set([WEBSITE_STEP, ...SCHEDULE_STEPS]);

// The category step hands off to the assistant instead of running a local
// generator. Bucketing teams is an inference problem (which of MY teams is
// "youth"?), and only the AI flow proposes the categories, their scheduling
// rules AND the team.bulk_set_category assignments as one approval — a local
// preset picker can create the buckets but leaves every team uncategorised.
// Same prompt the Settings → Team categories "Suggest with AI" button fires.
const AI_STEPS = new Set([CATEGORY_STEP, ...GENERATOR_STEPS]);

// Milestones populated by the async member/location import. While an import is
// running we don't surface these as an actionable "add …" step (that would
// invite the user to add manually mid-import); they show as loading and flip
// automatically when the import lands.
const IMPORT_KEYS = new Set(['first_team', 'first_members', 'import_locations']);

// SetupWizard is the gamified onboarding surface on Home. It reads the same
// gettingstarted progress as the shell-level checklist dialog (which stays for
// reopening from other pages), but presents it as a focused one-step-at-a-time
// stepper with confetti on each unlock. Later phases wire "generate in the
// background" answers into these steps; Phase 0 focuses the flow + celebration.
export function SetupWizard({ slug }: { slug: string }) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useGettingStarted(slug);
  const data = query.data;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: GETTING_STARTED_QUERY_KEY(slug) });

  const skipMutation = useMutation({
    mutationFn: (key: string) => skipGettingStartedStep(token, slug, key, true),
    onSuccess: invalidate,
  });
  const seenMutation = useMutation({
    mutationFn: () => markGettingStartedSeen(token, slug),
    onSuccess: invalidate,
  });
  const dismissMutation = useMutation({
    mutationFn: () => dismissGettingStarted(token, slug),
    onSuccess: invalidate,
  });

  const [focusKey, setFocusKey] = useState<string | null>(null);
  // Which in-wizard generator dialog is open (milestone key), or null.
  const [activeGenerator, setActiveGenerator] = useState<string | null>(null);
  // Let the user bring the wizard back after dismissing, without a round-trip.
  const [restored, setRestored] = useState(false);

  // Fire confetti whenever a core/optional milestone newly flips to done. Skip
  // the first data load (prevDone === null) so already-earned steps don't pop.
  const prevDone = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!data) return;
    const doneNow = new Set(data.milestones.filter((m) => m.done).map((m) => m.key));
    if (prevDone.current) {
      const newly = [...doneNow].filter((k) => !prevDone.current!.has(k));
      if (newly.length > 0) {
        fireConfetti(data.allCoreDone ? { particleCount: 160, spread: 150 } : {});
      }
    }
    prevDone.current = doneNow;
  }, [data]);

  const coreSteps = useMemo(
    () => (data ? data.milestones.filter((m) => m.core) : []),
    [data],
  );

  if (!data) return null;

  // Dismissed while still unfinished: a slim "resume setup" nudge instead of the
  // full card, unless the user restored it this session.
  if (data.dismissed && !data.allCoreDone && !restored) {
    return (
      <button
        type="button"
        onClick={() => setRestored(true)}
        className="inline-flex h-11 items-center gap-2 rounded-pill border border-border px-4 text-sm font-semibold text-foreground transition-transform hover:scale-[1.03] motion-reduce:transform-none"
      >
        <Rocket size={18} weight="bold" className="text-primary" />
        {t('setupWizard.resume')}
      </button>
    );
  }

  // All core steps done + acknowledged: the AchievementsCard takes over on Home.
  if (data.allCoreDone && !data.celebrate) return null;

  // Every core step done (or skipped), not yet acknowledged → celebration.
  if (data.celebrate) {
    return (
      <CelebrationPanel
        points={data.pointsTotal}
        pending={seenMutation.isPending}
        onDone={() => seenMutation.mutate()}
      />
    );
  }

  const pct = data.coreTotal > 0 ? Math.round((data.coreDone / data.coreTotal) * 100) : 0;

  // Focused step: the user's explicit pick if still actionable, else the first
  // unfinished core step (preferring not-skipped ones).
  const actionable = coreSteps.filter(
    (m) => !m.done && !(data.importing && IMPORT_KEYS.has(m.key)),
  );
  const current =
    actionable.find((m) => m.key === focusKey) ??
    actionable.find((m) => !m.skipped) ??
    actionable[0] ??
    null;
  const currentMeta = current ? MILESTONE_META[current.key] : undefined;
  const CurrentGlyph = currentMeta?.icon ?? Circle;

  return (
    <section className="w-full rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15">
          <Rocket size={22} weight="fill" className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-lg font-semibold tracking-tight">
            {t('setupWizard.title')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('setupWizard.subtitle')}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0"
          aria-label={t('setupWizard.dismiss')}
          onClick={() => dismissMutation.mutate()}
          disabled={dismissMutation.isPending}
        >
          <X size={18} />
        </Button>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <div
          className="h-2 flex-1 overflow-hidden rounded-pill bg-muted"
          role="progressbar"
          aria-valuenow={data.coreDone}
          aria-valuemin={0}
          aria-valuemax={data.coreTotal}
        >
          <div
            className="h-full rounded-pill bg-primary transition-[width] duration-500 motion-reduce:transition-none"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="shrink-0 font-heading text-sm font-semibold tabular-nums">
          {t('gettingStarted.progress', { done: data.coreDone, total: data.coreTotal })}
        </span>
      </div>

      {data.importing && (
        <div className="mt-5 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <CircleNotch
            size={22}
            weight="bold"
            className="shrink-0 animate-spin text-primary motion-reduce:animate-none"
            aria-hidden
          />
          <div className="min-w-0">
            <p className="font-heading text-sm font-semibold tracking-tight">
              {t('setupWizard.importing.title')}
            </p>
            <p className="text-sm text-muted-foreground">{t('setupWizard.importing.body')}</p>
          </div>
        </div>
      )}

      {current && currentMeta && (
        <div className="mt-6 flex flex-col items-start gap-4 rounded-xl border border-border bg-muted/40 p-5 sm:flex-row sm:items-center">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/15">
            <CurrentGlyph size={24} weight="bold" className="text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-base font-semibold tracking-tight">
              {t(`gettingStarted.milestone.${current.key}.title`)}
            </h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {t(`gettingStarted.milestone.${current.key}.description`)}
            </p>
          </div>
          <div className="flex w-full shrink-0 gap-2 sm:w-auto">
            <Button
              className="flex-1 sm:flex-none"
              onClick={() => {
                if (current.key === CATEGORY_STEP) {
                  askAssistant(t('settings.teamCategories.suggestAIPrompt'));
                } else if (GENERATOR_STEPS.has(current.key)) setActiveGenerator(current.key);
                else navigate(`/orgs/${slug}${currentMeta.to}`);
              }}
            >
              {AI_STEPS.has(current.key)
                ? t('setupWizard.generateWithAi')
                : t('setupWizard.startCta')}
              <ArrowRight size={16} weight="bold" />
            </Button>
            {!current.skipped && (
              <Button
                variant="outline"
                onClick={() => skipMutation.mutate(current.key)}
                disabled={skipMutation.isPending}
              >
                {t('gettingStarted.skip')}
              </Button>
            )}
          </div>
        </div>
      )}

      <ol className="mt-6 flex flex-wrap gap-2" aria-label={t('setupWizard.stepsAria')}>
        {coreSteps.map((m) => {
          const StepGlyph = MILESTONE_META[m.key]?.icon ?? Circle;
          const isCurrent = current?.key === m.key;
          const title = t(`gettingStarted.milestone.${m.key}.title`);
          const state = m.done ? 'done' : m.skipped ? 'skipped' : isCurrent ? 'current' : 'todo';
          return (
            <li key={m.key}>
              <button
                type="button"
                onClick={() => setFocusKey(m.key)}
                disabled={m.done}
                aria-label={title}
                aria-current={isCurrent ? 'step' : undefined}
                title={title}
                className={stepPillClass(state)}
              >
                {m.done ? (
                  <CheckCircle size={18} weight="fill" />
                ) : (
                  <StepGlyph size={18} weight="bold" />
                )}
              </button>
            </li>
          );
        })}
      </ol>

      {activeGenerator !== null && SCHEDULE_STEPS.has(activeGenerator) && (
        <ScheduleGenerator
          slug={slug}
          open
          onOpenChange={(o) => {
            if (!o) setActiveGenerator(null);
          }}
        />
      )}
      {activeGenerator === WEBSITE_STEP && (
        <WebsiteGenerator
          slug={slug}
          open
          onOpenChange={(o) => {
            if (!o) setActiveGenerator(null);
          }}
        />
      )}
    </section>
  );
}

type StepState = 'done' | 'current' | 'skipped' | 'todo';

function stepPillClass(state: StepState): string {
  const base =
    'inline-flex h-11 w-11 items-center justify-center rounded-pill transition-transform hover:scale-[1.04] disabled:cursor-default disabled:hover:scale-100 motion-reduce:transform-none';
  switch (state) {
    case 'done':
      return `${base} bg-primary/15 text-primary`;
    case 'current':
      return `${base} bg-primary text-primary-foreground`;
    case 'skipped':
      return `${base} bg-muted text-muted-foreground/50`;
    default:
      return `${base} bg-muted text-muted-foreground hover:bg-muted/70`;
  }
}

function CelebrationPanel({
  points,
  pending,
  onDone,
}: {
  points: number;
  pending: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation('common');
  useEffect(() => {
    fireConfetti({ particleCount: 180, spread: 160 });
  }, []);
  return (
    <section className="flex w-full flex-col items-center rounded-2xl border border-border bg-card px-6 py-10 text-center shadow-sm">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/15">
        <Sparkle size={34} weight="fill" className="text-primary" />
      </div>
      <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight">
        {t('gettingStarted.celebrate.title')}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        {t('gettingStarted.celebrate.body')}
      </p>
      <p className="mt-3 font-heading text-sm font-semibold text-primary">
        {t('gettingStarted.pointsLabel', { points })}
      </p>
      <Button className="mt-6" onClick={onDone} disabled={pending}>
        {t('gettingStarted.celebrate.cta')}
      </Button>
    </section>
  );
}
